import { patientService } from "../../services/patientService";
import { patientAllergyService } from "../../services/patientAllergyService";
import { patientClinicalScoreService } from "../../services/patientClinicalScoreService";
import { patientDocumentService } from "../../services/patientDocumentService";
import { patientImplantService } from "../../services/patientImplantService";
import { patientRecordService } from "../../services/patientRecordService";
import { patientRehabProtocolService } from "../../services/patientRehabProtocolService";
import { patientMedicineService } from "../../services/patientMedicineService";
import { patientSurgeryService } from "../../services/patientSurgeryService";

// Εισαγωγή τύπων πεδίων και data
import {
  getPatientAllergyFields,
  patientAllergyFormDataKeys,
} from "../../types/patientAllergy";
import {
  patientClinicalScoreFields,
  patientClinicalScoreFormData,
} from "../../types/patientClinicalScore";
import {
  patientDocumentCreateFields,
  patientDocumentFormData,
} from "../../types/patientDocument";
import {
  getPatientImplantFields,
  patientImplantFormData,
} from "../../types/patientImplant";
import { getPatientRecordFields } from "../../types/patientRecord";
import { getPatientRehabProtocolFields } from "../../types/patientRehabProtocol";
import { getPatientMedicineFields } from "../../types/patientMedicine";
import { getPatientSurgeryFields } from "../../types/patientSurgery";
import { patientFormData } from "../../types/patient";

export type FieldDefinition = {
  readonly name: string;
  readonly label: string;
  readonly type: string;
  readonly required?: boolean;
  readonly options?: { label: string; value: string | number }[];
};

export type Category =
  | "patient"
  | "patientAllergy"
  | "patientClinicalScore"
  | "patientDocument"
  | "patientImplant"
  | "patientRecord"
  | "patientRehabProtocol"
  | "patientMedicine"
  | "patientSurgery";

export const patientServicesMap: Record<Category, any> = {
  patient: patientService,
  patientAllergy: patientAllergyService,
  patientClinicalScore: patientClinicalScoreService,
  patientDocument: patientDocumentService,
  patientImplant: patientImplantService,
  patientRecord: patientRecordService,
  patientRehabProtocol: patientRehabProtocolService,
  patientMedicine: patientMedicineService,
  patientSurgery: patientSurgeryService,
};

export {
  patientServicesMap as searchServiceMap,
  patientServicesMap as deleteServiceMap,
  patientServicesMap as updateServiceMap,
};

// 1. Για τα πεδία της φόρμας (strings / keys)
export const PatientEntityFormFields: Partial<
  Record<Category, readonly string[]>
> = {
  patient: patientFormData,
  patientAllergy: patientAllergyFormDataKeys,
  patientClinicalScore: patientClinicalScoreFormData,
  patientDocument: patientDocumentFormData,
  patientImplant: patientImplantFormData,
};

// 2. Για τις στήλες του πίνακα ή τα πλήρη definitions με Labels (Factories)
export const PatientEntityFieldsMap: Record<
  Category,
  | readonly FieldDefinition[]
  | ((options?: any, surgeries?: any) => FieldDefinition[])
> = {
  patient: patientFormData as any,
  patientAllergy: getPatientAllergyFields,
  patientClinicalScore: patientClinicalScoreFields,
  patientDocument: patientDocumentCreateFields, // ✅ Factory function που παίρνει (documentTypes, surgeries)
  patientImplant: getPatientImplantFields,
  patientRecord: getPatientRecordFields,
  patientRehabProtocol: getPatientRehabProtocolFields,
  patientMedicine: getPatientMedicineFields,
  patientSurgery: getPatientSurgeryFields,
};

export const patientCategoryLabels: Record<Category | string, string> = {
  patient: "Βασικά Στοιχεία",
  patientAllergy: "Αλλεργίες",
  patientClinicalScore: "Κλινικά Scores",
  patientDocument: "Αποτελέσματα Εξετάσεων",
  patientImplant: "Εμφυτεύματα",
  patientRecord: "Ιστορικό / Αρχεία",
  patientRehabProtocol: "Πρωτόκολλα Αποκατάστασης",
  patientMedicine: "Φαρμακευτικές Αγωγές",
  patientSurgery: "Χειρουργεία",
};

export const getEntityFields = (
  category: Category,
  isCreateMode: boolean = false,
  options: any = [],
): FieldDefinition[] => {
  const fieldsConfig = PatientEntityFieldsMap[category];

  if (typeof fieldsConfig === "function") {
    // Ειδικός χειρισμός για το patientDocument αν σταλεί Object payload
    if (category === "patientDocument") {
      if (options && !Array.isArray(options)) {
        return fieldsConfig(
          options.documentTypes || [],
          options.surgeries || [],
        );
      }
      return fieldsConfig(options || [], []);
    }

    return fieldsConfig(options);
  }

  return (fieldsConfig as FieldDefinition[]) || [];
};
